import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchInventoryBins, fetchInventoryItems } from '../api';
import { BinDialog, BinEditDialog, HistoryDialog, PowderFormDialog, ReceiveDialog, WeighDialog } from './PowderDialogs';
import { compareText, daysSince, formatDate, formatQuantity, toNumber } from './inventoryFormat';
import './powderinventory.css';

// Powder coat inventory: what's in each bin and how much is left. People weigh the
// bag to know what's left, so "Weigh" is the main action; "Received" adds a delivery
// as a new bag. A powder can sit in more than one bag, and the tag makes that obvious.

const VIEWS = [
  { key: 'list', label: 'List' },
  { key: 'bins', label: 'Bins' },
  { key: 'reorder', label: 'Reorder' },
];

const SORTS = {
  category: (a, b) => compareText(a.category, b.category) || compareText(a.name, b.name),
  name: (a, b) => compareText(a.name, b.name),
  bin: (a, b) => compareText(a.bin || '~', b.bin || '~') || compareText(a.name, b.name),
  quantity: (a, b) => (toNumber(a.quantity) ?? -1) - (toNumber(b.quantity) ?? -1) || compareText(a.name, b.name),
  weighed: (a, b) => String(a.last_counted_at || '').localeCompare(String(b.last_counted_at || '')) || compareText(a.name, b.name),
};

const COLUMNS = [
  { key: 'bin', label: 'Bin' },
  { key: 'name', label: 'Color' },
  { key: 'category', label: 'Category' },
  { key: 'quantity', label: 'Lbs' },
  { key: 'weighed', label: 'Last weighed' },
];

const uniqueSorted = (values) => [...new Set(values.filter(Boolean))].sort(compareText);
const sortBins = (data) => [...data].sort((a, b) => compareText(a.name, b.name));

const Quantity = ({ item }) => {
  if (item.quantity === null) return <span className="inv-qty inv-qty-none">—</span>;
  return <span className={`inv-qty ${item.is_low ? 'inv-qty-low' : ''}`}>{formatQuantity(item.quantity)}</span>;
};

const BagsTag = ({ item }) => (item.bags && item.bags.length > 1 ? (
  <span className="inv-tag inv-tag-bags" title="Weigh each bag on its own">{item.bags.length} bags</span>
) : null);

const ProductLink = ({ item }) => (item.product_url ? (
  <a className="inv-product-link" href={item.product_url} target="_blank" rel="noopener noreferrer" title="Open the product page" onClick={(event) => event.stopPropagation()}>↗</a>
) : null);

function PowderInventory() {
  const [items, setItems] = useState(null);
  const [binList, setBinList] = useState([]);
  const [error, setError] = useState('');
  const [view, setView] = useState('list');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [bin, setBin] = useState('');
  const [lowOnly, setLowOnly] = useState(false);
  const [showRetired, setShowRetired] = useState(false);
  const [sort, setSort] = useState({ key: 'category', desc: false });
  // { type: 'weigh' | 'receive' | 'edit' | 'bin' | 'add' | 'details', itemId }, or { type: 'edit-bin', bin }
  const [dialog, setDialog] = useState(null);

  const load = useCallback(() => {
    Promise.all([
      fetchInventoryItems({ kind: 'powder', ...(showRetired ? { include_inactive: '1' } : {}) }),
      fetchInventoryBins({ kind: 'powder' }),
    ])
      .then(([itemData, binData]) => { setItems(itemData); setBinList(sortBins(binData)); setError(''); })
      .catch(() => setError('Could not load the powder inventory.'));
  }, [showRetired]);

  useEffect(() => { load(); }, [load]);

  const all = useMemo(() => items || [], [items]);
  const categories = useMemo(() => uniqueSorted(all.map((item) => item.category)), [all]);
  const suppliers = useMemo(() => uniqueSorted(all.map((item) => item.supplier)), [all]);

  const filtered = useMemo(() => {
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return all.filter((item) => {
      if (category && item.category !== category) return false;
      if (bin && item.bin !== bin) return false;
      if (lowOnly && !item.is_low) return false;
      if (!words.length) return true;
      const text = [item.name, item.category, item.part_number, item.supplier, item.notes, item.bin && `bin ${item.bin}`].join(' ').toLowerCase();
      return words.every((word) => text.includes(word));
    });
  }, [all, search, category, bin, lowOnly]);

  const sorted = useMemo(() => {
    const list = [...filtered].sort(SORTS[sort.key]);
    return sort.desc ? list.reverse() : list;
  }, [filtered, sort]);

  const totals = useMemo(() => {
    const active = all.filter((item) => item.is_active);
    return {
      count: active.length,
      low: active.filter((item) => item.is_low).length,
      pounds: active.reduce((sum, item) => sum + (toNumber(item.quantity) || 0), 0),
    };
  }, [all]);

  const dialogItem = dialog && dialog.itemId ? all.find((item) => item.id === dialog.itemId) : null;
  const open = (type, item) => setDialog({ type, itemId: item ? item.id : null });
  const close = () => setDialog(null);

  const saved = (updated) => {
    // A powder that changed bins changes the bins' counts (which decide whether one can be deleted).
    const before = (items || []).find((item) => item.id === updated.id);
    if (!before || before.bin !== updated.bin) {
      fetchInventoryBins({ kind: 'powder' }).then((data) => setBinList(sortBins(data))).catch(() => {});
    }
    setItems((current) => {
      const list = current || [];
      const exists = list.some((item) => item.id === updated.id);
      const next = exists ? list.map((item) => (item.id === updated.id ? updated : item)) : [...list, updated];
      return showRetired ? next : next.filter((item) => item.is_active);
    });
    // Back to the details after a change made from there; otherwise done.
    setDialog((current) => (current && current.from === 'details' && updated.is_active ? { type: 'details', itemId: updated.id } : null));
  };

  // Renaming a bin changes what every powder in it shows, so reload everything.
  const binSaved = () => { setDialog(null); load(); };

  const fromDetails = (type) => setDialog((current) => ({ type, itemId: current.itemId, from: 'details' }));

  const toggleSort = (key) => setSort((current) => ({ key, desc: current.key === key ? !current.desc : false }));

  const filtersActive = Boolean(search || category || bin || lowOnly);
  const clearFilters = () => { setSearch(''); setCategory(''); setBin(''); setLowOnly(false); };

  let body;
  if (error) {
    body = <div className="inv-error">{error} <button type="button" className="inv-link" onClick={load}>Try again</button></div>;
  } else if (!items) {
    body = <div className="inv-empty">Loading…</div>;
  } else if (view === 'bins' && (filtered.length || !filtersActive)) {
    // Empty bins show too, unless a filter is narrowing things down.
    body = <BinsView items={filtered} bins={binList} showEmpty={!filtersActive} onOpen={open} onEditBin={(b) => setDialog({ type: 'edit-bin', bin: b })} />;
  } else if (!all.length) {
    body = <div className="inv-empty">No powders yet. Use “Add powder” to start.</div>;
  } else if (!filtered.length) {
    body = <div className="inv-empty">Nothing matches. <button type="button" className="inv-link" onClick={clearFilters}>Clear filters</button></div>;
  } else if (view === 'reorder') {
    body = <ReorderView items={filtered} onOpen={open} />;
  } else {
    body = <ListView items={sorted} sort={sort} onSort={toggleSort} onOpen={open} />;
  }

  return (
    <div className="inv-page">
      <div className="inv-top">
        <div>
          <h2 className="inv-title">Powder Inventory</h2>
          {items && (
            <div className="inv-muted">
              {totals.count} powders · <span className="inv-low-text">{totals.low} low</span> · {formatQuantity(totals.pounds)} lbs on hand
            </div>
          )}
        </div>
        <div className="inv-top-actions">
          {view !== 'list' && <button type="button" className="inv-button inv-print-hide" onClick={() => window.print()}>Print</button>}
          {view === 'bins' && <button type="button" className="inv-button" onClick={() => setDialog({ type: 'edit-bin', bin: null })}>+ Add bin</button>}
          <button type="button" className="inv-button inv-button-primary" onClick={() => open('add')}>+ Add powder</button>
        </div>
      </div>

      <div className="inv-tabs" role="tablist">
        {VIEWS.map((v) => (
          <button key={v.key} type="button" role="tab" aria-selected={view === v.key} className={`inv-tab ${view === v.key ? 'active' : ''}`} onClick={() => setView(v.key)}>
            {v.label}
          </button>
        ))}
      </div>

      <div className="inv-filters">
        <input className="inv-input inv-search" type="search" placeholder="Search color, part #, bin…" value={search} onChange={(event) => setSearch(event.target.value)} />
        <select className="inv-input" value={category} onChange={(event) => setCategory(event.target.value)} aria-label="Category">
          <option value="">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="inv-input" value={bin} onChange={(event) => setBin(event.target.value)} aria-label="Bin">
          <option value="">All bins</option>
          {binList.map((b) => <option key={b.id} value={b.name}>Bin {b.name}</option>)}
        </select>
        {view !== 'reorder' && (
          <label className="inv-check">
            <input type="checkbox" checked={lowOnly} onChange={(event) => setLowOnly(event.target.checked)} /> Low only
          </label>
        )}
        <label className="inv-check">
          <input type="checkbox" checked={showRetired} onChange={(event) => setShowRetired(event.target.checked)} /> Show not stocked
        </label>
        {filtersActive && <button type="button" className="inv-link" onClick={clearFilters}>Clear</button>}
      </div>

      {body}

      {dialog && dialog.type === 'add' && (
        <PowderFormDialog bins={binList} categories={categories} suppliers={suppliers} onClose={close} onSaved={saved} />
      )}
      {dialogItem && dialog.type === 'edit' && (
        <PowderFormDialog item={dialogItem} bins={binList} categories={categories} suppliers={suppliers} onClose={close} onSaved={saved} />
      )}
      {dialogItem && dialog.type === 'weigh' && <WeighDialog item={dialogItem} onClose={close} onSaved={saved} />}
      {dialogItem && dialog.type === 'bin' && <BinDialog item={dialogItem} bins={binList} onClose={close} onSaved={saved} />}
      {dialog && dialog.type === 'edit-bin' && <BinEditDialog bin={dialog.bin} onClose={close} onSaved={binSaved} />}
      {dialogItem && dialog.type === 'receive' && <ReceiveDialog item={dialogItem} onClose={close} onSaved={saved} />}
      {dialogItem && dialog.type === 'details' && <HistoryDialog item={dialogItem} onClose={close} onAction={fromDetails} />}

      {/* What Print prints: a copy of the view outside the app's scrolling layout, so
          every bin makes it onto paper (see the print styles). */}
      {view !== 'list' && items && createPortal(
        <div className="inv-page inv-print-sheet">
          <h2 className="inv-title">{view === 'bins' ? 'Powder bins' : 'Powder to reorder'}</h2>
          <div className="inv-muted">Printed {formatDate(new Date())} · {totals.count} powders · {totals.low} low</div>
          {body}
        </div>,
        document.body,
      )}
    </div>
  );
}

const ListView = ({ items, sort, onSort, onOpen }) => (
  <div className="inv-list">
    <div className="inv-row inv-row-head">
      {COLUMNS.map((column) => (
        <button key={column.key} type="button" className={`inv-sort inv-col-${column.key}`} onClick={() => onSort(column.key)}>
          {column.label}
          {sort.key === column.key && <span aria-hidden="true">{sort.desc ? ' ▼' : ' ▲'}</span>}
        </button>
      ))}
      <span className="inv-col-actions" />
    </div>
    {items.map((item) => {
      const age = daysSince(item.last_counted_at);
      return (
        <div key={item.id} className={`inv-row ${item.is_low ? 'inv-row-low' : ''} ${item.is_active ? '' : 'inv-row-retired'}`}>
          <span className="inv-col-bin">{item.bin ? item.bin : <span className="inv-muted">—</span>}</span>
          <span className="inv-col-name">
            <button type="button" className="inv-name" onClick={() => onOpen('details', item)}>{item.name}</button>
            <ProductLink item={item} />
            <BagsTag item={item} />
            {!item.is_active && <span className="inv-tag">Not stocked</span>}
          </span>
          <span className="inv-col-category">{item.category}</span>
          <span className="inv-col-quantity"><Quantity item={item} /><span className="inv-unit"> lbs</span></span>
          <span className={`inv-col-weighed ${age !== null && age > 90 ? 'inv-stale' : ''}`} title={age !== null ? `${age} days ago` : ''}>
            {item.last_counted_at ? formatDate(item.last_counted_at) : <span className="inv-muted">never</span>}
          </span>
          <span className="inv-col-actions">
            <button type="button" className="inv-button inv-button-primary inv-button-small" onClick={() => onOpen('weigh', item)}>Weigh</button>
            <button type="button" className="inv-button inv-button-small" onClick={() => onOpen('receive', item)}>Received</button>
          </span>
        </div>
      );
    })}
  </div>
);

const BinsView = ({ items, bins, showEmpty, onOpen, onEditBin }) => {
  const groups = useMemo(() => {
    const byBin = new Map(showEmpty ? bins.map((b) => [b.name, { key: b.name, bin: b, items: [] }]) : []);
    items.forEach((item) => {
      const key = item.bin || '';
      if (!byBin.has(key)) byBin.set(key, { key, bin: bins.find((b) => b.name === key) || null, items: [] });
      byBin.get(key).items.push(item);
    });
    return [...byBin.values()]
      .sort((a, b) => (a.key === '' ? 1 : b.key === '' ? -1 : compareText(a.key, b.key)))
      .map((group) => ({
        ...group,
        items: group.items.sort(SORTS.name),
        label: (group.bin && group.bin.description) || uniqueSorted(group.items.map((item) => item.category)).join(' / '),
      }));
  }, [items, bins, showEmpty]);

  return (
    <div className="inv-bins">
      {groups.map((group) => (
        <section key={group.key || 'none'} className="inv-bin">
          <div className="inv-bin-head">
            <strong>{group.key ? `Bin ${group.key}` : 'No bin'}</strong>
            <span className="inv-bin-categories">{group.label}</span>
            {group.bin && (
              <button type="button" className="inv-bin-edit inv-print-hide" onClick={() => onEditBin(group.bin)} title={`Edit bin ${group.key}`} aria-label={`Edit bin ${group.key}`}>
                Edit
              </button>
            )}
          </div>
          {group.items.map((item) => (
            <button key={item.id} type="button" className="inv-bin-item" onClick={() => onOpen('bin', item)} title="Change bin">
              <span>{item.name}<BagsTag item={item} /></span>
              <Quantity item={item} />
            </button>
          ))}
          {!group.items.length && <div className="inv-bin-empty">Empty</div>}
        </section>
      ))}
    </div>
  );
};

const ReorderView = ({ items, onOpen }) => {
  const low = items.filter((item) => item.is_low).sort((a, b) => SORTS.quantity(a, b));
  const unweighed = items.filter((item) => item.quantity === null).sort(SORTS.name);
  const bySupplier = new Map();
  low.forEach((item) => {
    const key = item.supplier || 'No supplier';
    if (!bySupplier.has(key)) bySupplier.set(key, []);
    bySupplier.get(key).push(item);
  });
  const groups = [...bySupplier.entries()].sort(([a], [b]) => (a === 'No supplier' ? 1 : b === 'No supplier' ? -1 : compareText(a, b)));

  return (
    <div className="inv-reorder">
      <div className="inv-muted inv-reorder-intro">
        {low.length ? `${low.length} powder${low.length === 1 ? ' is' : 's are'} at or below the low-stock level, lowest first.` : 'Nothing is low right now.'}
      </div>
      {groups.map(([supplier, list]) => (
        <section key={supplier} className="inv-reorder-group">
          <div className="inv-section-title">{supplier} ({list.length})</div>
          {list.map((item) => (
            <div key={item.id} className="inv-reorder-row">
              <span>
                <button type="button" className="inv-name" onClick={() => onOpen('details', item)}>{item.name}</button>
                <BagsTag item={item} />
              </span>
              <span className="inv-muted">{item.part_number}</span>
              <span><Quantity item={item} /> <span className="inv-muted">/ {formatQuantity(item.low_stock_level)} lbs</span></span>
              <span className="inv-muted">{item.bin ? `Bin ${item.bin}` : ''}</span>
              {item.product_url ? <a href={item.product_url} target="_blank" rel="noopener noreferrer" className="inv-print-hide">Order ↗</a> : <span />}
            </div>
          ))}
        </section>
      ))}
      {unweighed.length > 0 && (
        <section className="inv-reorder-group">
          <div className="inv-section-title">Never weighed ({unweighed.length})</div>
          {unweighed.map((item) => (
            <div key={item.id} className="inv-reorder-row">
              <button type="button" className="inv-name" onClick={() => onOpen('weigh', item)}>{item.name}</button>
              <span className="inv-muted">{item.part_number}</span>
              <span className="inv-muted">—</span>
              <span className="inv-muted">{item.bin ? `Bin ${item.bin}` : ''}</span>
              <span />
            </div>
          ))}
        </section>
      )}
    </div>
  );
};

export default PowderInventory;
